import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';

// utils
import axios from '../utils/axios';
import { setSession } from '../utils/jwt';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  errorData: '',
  user: null
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  },
  LOGIN: (state, action) => {
    const payloadData = action?.payload;
    let payloadState = {}
    if(payloadData?.user) {
      payloadState = {
        ...state,
        isAuthenticated: true,
        user: payloadData?.user,
      };
    } else {
      payloadState = {
        ...state,
        isAuthenticated: false,
        errorData: payloadData?.isError,
        user: null,
      };
    }
    return payloadState
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state, action) => {
    const payloadData = action?.payload;
    let payloadState = {}
    if(payloadData?.user) {
      payloadState = {
        ...state,
        isAuthenticated: true,
        user: payloadData?.user,
      };
    } else {
      payloadState = {
        ...state,
        isAuthenticated: false,
        errorData: payloadData?.isError,
        user: null,
      };
    }
    return payloadState
  },

  UPDATEPROFILE: (state, action) => {
    const { user }  = action?.payload;

    return {
      ...state,
      user
    };
  }
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
});

AuthProvider.propTypes = {
  children: PropTypes.node
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        if (accessToken) {
          await setSession(accessToken);
          const response  = await axios.get('/me');
          const resData = response.data; let userData = null; let isAuthenticated = false;
          if(resData) {
            userData = resData.data;
            userData.displayName = (userData.firstName && userData.firstName !== null) ? `${userData.firstName} ${userData.lastName}` : userData.profileName;
            isAuthenticated = true;
          }
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated,
              user: userData,
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (formData) => {
    formData.platform = 'browser'
    const response = await axios.post('/login?web', formData);
    const resData = response?.data; let userData = null;
    if(resData && resData.success){
      userData = resData?.data;
      // const fullToken = (process.env.REACT_APP_ENV === 'prod') ? 'yes' : JSON.stringify({accessToken: resData?.token, refresh: resData?.refreshToken});
      const fullToken = JSON.stringify({accessToken: resData?.token, refresh: resData?.refreshToken});
      await setSession(fullToken);
      userData.displayName = (userData?.firstName && userData?.firstName !== null) ? `${userData?.firstName} ${userData?.lastName}` : userData?.profileName;
    }
    dispatch({
      type: 'LOGIN',
      payload: {
        isError: resData,
        user: userData,
      },
    });
    return resData || response;
  };

  const register = async (formData) => {
    const response = await axios.post('/ambassador/register', formData);
    const resData = response.data;
    if(resData && resData.success){
      const profileData  = resData.data;
      let fNamelName = (profileData.firstName) ? profileData.firstName : '';
      fNamelName += (profileData.lastName) ? ' '+profileData.lastName : '';
      if(resData.token) {
        const user = {
            id: profileData._id,
            displayName: (fNamelName) ? fNamelName : profileData.profileName,
            profileName: profileData.profileName,
            first_name: profileData.firstName,
            last_name: profileData.lastName,
            email: profileData.email,
            gender: profileData.gender,
            about: profileData.aboutMe,
            photoURL: (profileData.avatar) ? profileData.avatar.path : null,
            avatarId: (profileData.avatar) ? profileData.avatar.id : '',
            role: profileData.role,
            isPublic: true
          };
        setSession(resData.token);

        dispatch({
          type: 'REGISTER',
          payload: {
            user
          }
        });
      }
    }
    return resData;
  };

  const logout = async () => {
    await axios.get('/logout');
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const resetPassword = async ( email ) => {
    const response = await axios.post('/forgot-password', { email });
    return response.data || response;
  };

  const recoverPassword = async (reqToken, password) => {
    const response = await axios.post(`/reset-password/${reqToken}`, { password });
    return response.data || response;
  }

  const updateProfile = async (formData) => {
    const response = await axios.put('/me', formData);
    const resData = response?.data;
    if(resData && resData?.success){
      const userData = resData?.data;
      userData.displayName = (userData?.firstName && userData?.firstName !== null) ? `${userData?.firstName} ${userData?.lastName}` : userData?.profileName;
      dispatch({
        type: 'UPDATEPROFILE',
        payload: {
          user: userData,
        },
      });
    }
    return resData || response;
  };



  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        resetPassword,
        recoverPassword,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
